
import { getHubData } from '@/app/infrastructures/misc/Cookies';
import CategorizedSelector, {
  CategorizedItem,
  Category,
  FieldConfig
} from '@/app/ui/components/categorized-selector/index.vue';
import { AccountController } from '@/app/ui/controllers/AccountController';
import { DispatchController } from '@/app/ui/controllers/DispatchController';
import { CourierShuttle, CourierShuttleListRequest } from '@/domain/entities/CourierShuttle';
import { Options, prop, Vue } from 'vue-class-component';

class Props {
  selectedItem = prop<CategorizedItem | null>({ default: null });
  disabled = prop<boolean>({ default: false });
}

@Options({
  components: {
    CategorizedSelector
  },
  emits: ["update:selectedItem", "courierShuttleChange"],
  watch: {
    selectedItem: {
      handler(newVal) {
        this.internalSelectedItem = newVal;
      },
      immediate: true
    },
  }
})

export default class CourierShuttleSelector extends Vue.with(Props) {
  internalSelectedItem: CategorizedItem | null = null;
  fieldConfigs: Record<string, FieldConfig[]> = {
    courier: [
      {
        key: "phone",
        displayKey: "phone",
        className: "text-sm text-gray-500 mt-1",
        showWhen: (item: CategorizedItem) => !!item.phone
      }
    ],
    shuttle: [
      {
        key: "hubName",
        displayKey: "hubName",
        className: "text-sm text-gray-500 mt-1",
        showWhen: (item: CategorizedItem) => !!item.hubName
      }
    ]
  };

  apiData: Record<string, CategorizedItem[]> = {};

  // Pagination state
  paginationState: Record<
    string,
    { page: number; hasMore: boolean; loading: boolean }
  > = {
    courier: { page: 1, hasMore: true, loading: false },
    shuttle: { page: 1, hasMore: true, loading: false }
  };

  // Search state
  currentSearchQuery = "";

  get courierShuttleCategories(): Category[] {
    const categories = [
      {
        key: "courier",
        label: this.$t("dispatch.courier"),
        icon: require("@/app/ui/assets/svg/ics_o_person.svg"),
        emptyText: this.$t("dispatch.courierNotFound"),
        emptyImage: require("@/app/ui/assets/svg/ill_spot_da_out_of_range.svg"),
        fields: this.fieldConfigs.courier
      },
      {
        key: "shuttle",
        label: "Shuttle",
        icon: require("@/app/ui/assets/svg/ics_o_truck.svg"),
        emptyText: this.$t("dispatch.shuttleNotFound"),
        emptyImage: require("@/app/ui/assets/svg/ill_spot_da_out_of_range.svg"),
        fields: this.fieldConfigs.shuttle
      }
    ];

    if (this.shuttleId) {
      return [categories[0]]; // Only return Kurir category
    }

    return categories;
  }
  
  get shuttleId(): number | null {
    return AccountController.accountData.shuttleId || null;
  }

  get partnerId(): number | null {
    return AccountController.accountData.account_type_detail.id || null;
  }
  
  get headerText(): string {
    if (!this.shuttleId) {
      return this.$t("dispatch.selectCourierOrShuttle");
    } else {
      return this.$t("dispatch.selectCourier");
    }
  }

  get placeholder(): string {
    if (!this.shuttleId) {
      return this.$t("dispatch.selectCourierOrShuttle");
    } else {
      return this.$t("dispatch.selectCourier");
    }
  }

  get searchPlaceholder(): string {
    if (this.shuttleId) {
      return this.$t("dispatch.searchCourier");
    }
    return this.$t("dispatch.searchCourierOrShuttle");
  }

  handleSearch(query: string) {
    return this.searchCourierShuttle(query);
  }

  handleCourierShuttleChange(item: CategorizedItem) {
    this.internalSelectedItem = item;
    this.$emit("update:selectedItem", item);
    this.$emit("courierShuttleChange", item);
  }

  async loadCourierShuttleData(tabKey: string): Promise<CategorizedItem[]> {
    try {
      const dataType = this.getDataTypeFromTabKey(tabKey);

      if (!dataType) {
        return [];
      }

      if (!this.partnerId) {
        return [];
      }

      const pagination = this.paginationState[dataType];

      if (pagination.loading || !pagination.hasMore) {
        return [];
      }

      pagination.loading = true;

      const response = await DispatchController.getCourierShuttleList(
        new CourierShuttleListRequest({
          page: pagination.page,
          limit: 20,
          dataType: dataType as "courier" | "shuttle",
          hubId: this.getAccountHubId()
        })
      );

      const categorizedItems = response.data.map((item) => ({
        id: item.id,
        name: item.name,
        phone: item.phoneNumber,
        hubId: item.hubId,
        hubName: item.hubName,
        shuttleId: item.shuttleId,
        shuttleName: item.shuttleName,
        cityCode: item.cityCode,
        dataType: item.dataType,
        label: item.dataType === "courier" ? `${item.id} - ${item.name}` 
          : item.dataType === "shuttle" ? `${item.shuttleName} - ${item.cityCode}` : '-'
      }));

      if (!this.apiData[dataType]) {
        this.apiData[dataType] = [];
      }
      this.apiData[dataType] = [...this.apiData[dataType], ...categorizedItems];

      pagination.page += 1;
      pagination.hasMore = response.meta.page < response.meta.totalPages;
      pagination.loading = false;

      return categorizedItems;
    } catch (error) {
      const dataType = this.getDataTypeFromTabKey(tabKey);
      if (dataType) {
        this.paginationState[dataType].loading = false;
      }
      return [];
    }
  }

  private getDataTypeFromTabKey(tabKey: string): string | null {
    const dataTypeMap: Record<string, string> = {
      courier: "courier",
      shuttle: "shuttle"
    };
    return dataTypeMap[tabKey] || null;
  }

  async searchCourierShuttle(query: string): Promise<Record<string, CategorizedItem[]>> {
    this.currentSearchQuery = query.trim();
    if (!this.currentSearchQuery) {
      return {};
    }

    try {
      const res = await DispatchController.getCourierShuttleList(
        new CourierShuttleListRequest({
          page: 1,
          limit: 20,
          q: query,
          hubId: this.getAccountHubId()
        })
      )
      if (!res.data.length) return {};

      const groupedResults: Record<string, CategorizedItem[]> = {};
      const categories = this.courierShuttleCategories;

      res.data.forEach((item: CourierShuttle) => {
        const categorizedItem = {
          id: item.id,
          name: item.name,
          phone: item.phoneNumber,
          hubId: item.hubId,
          hubName: item.hubName,
          shuttleId: item.shuttleId,
          shuttleName: item.shuttleName,
          cityCode: item.cityCode,
          dataType: item.dataType,
          label: item.dataType === "courier" ? `${item.id} - ${item.name}` 
            : item.dataType === "shuttle" ? `${item.shuttleName} - ${item.cityCode}` : '-'
        };
        if (item.dataType) {
          if (!groupedResults[item.dataType]) {
            groupedResults[item.dataType] = [];
          }
          groupedResults[item.dataType].push(categorizedItem);
        }
      });

      categories.forEach((category) => {
        this.paginationState[category.key] = {
          page: 2,
          hasMore: res.meta.page < res.meta.totalPages,
          loading: false
        };
      });

      return groupedResults;
    } catch (error) {
      return {};
    }
  }

  async loadMoreData(tabKey: string): Promise<CategorizedItem[]> {
    if (this.currentSearchQuery) {
      return await this.loadMoreSearchData(tabKey);
    }
    return await this.loadCourierShuttleData(tabKey);
  }

  async loadMoreSearchData(tabKey: string): Promise<CategorizedItem[]> {
    try {
      const dataType = this.getDataTypeFromTabKey(tabKey);
      if (!dataType) {
        return [];
      }
      if (!this.partnerId) {
        return [];
      }
      const pagination = this.paginationState[dataType];
      if (pagination.loading || !pagination.hasMore) {
        return [];
      }
      pagination.loading = true;

      const response = await DispatchController.getCourierShuttleList(
        new CourierShuttleListRequest({
          page: pagination.page,
          limit: 20,
          q: this.currentSearchQuery,
          dataType: dataType as "courier" | "shuttle",
          hubId: this.getAccountHubId()
        })
      );

      const categorizedItems = response.data.map((item) => ({
        id: item.id,
        name: item.name,
        phone: item.phoneNumber,
        hubId: item.hubId,
        hubName: item.hubName,
        shuttleId: item.shuttleId,
        shuttleName: item.shuttleName,
        cityCode: item.cityCode,
        dataType: item.dataType,
        label: item.dataType === "courier" ? `${item.id} - ${item.name}` 
          : item.dataType === "shuttle" ? `${item.shuttleName} - ${item.cityCode}` : '-'
      }));

      pagination.page += 1;
      pagination.hasMore = response.meta.page < response.meta.totalPages;
      pagination.loading = false;

      return categorizedItems;
    } catch (error) {
      const dataType = this.getDataTypeFromTabKey(tabKey);
      if (dataType) {
        this.paginationState[dataType].loading = false;
      }
      return [];
    }
  }

  resetPagination() {
    this.paginationState = {
      courier: { page: 1, hasMore: true, loading: false },
      shuttle: { page: 1, hasMore: true, loading: false }
    };
  }

  resetData() {
    this.currentSearchQuery = "";
    this.resetPagination();
  }

  hasMoreData(tabKey: string): boolean {
    const dataType = this.getDataTypeFromTabKey(tabKey);
    if (!dataType) return false;

    const pagination = this.paginationState[dataType];
    return pagination.hasMore && !pagination.loading;
  }

  isLoadingData(tabKey: string): boolean {
    const dataType = this.getDataTypeFromTabKey(tabKey);
    if (!dataType) return false;

    const pagination = this.paginationState[dataType];
    return pagination.loading;
  }

  getAccountHubId() {
    return getHubData()?.hubId || 0
  }

  get isHideTab() {
    return this.courierShuttleCategories.length === 1;
  }
}
